import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputBase,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import { useStatusPalette } from '../../utils/statusColors';

const DAY_OPTIONS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

const INTERVAL_UNITS = [
  { value: 'm', label: 'Minutes' },
  { value: 'h', label: 'Hours' },
  { value: 'd', label: 'Days' },
  { value: 'bd', label: 'Business days' },
];

const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'US Eastern' },
  { value: 'America/Chicago', label: 'US Central' },
  { value: 'America/Denver', label: 'US Mountain' },
  { value: 'America/Los_Angeles', label: 'US Pacific' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris / Berlin' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Shanghai', label: 'Shanghai' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Australia/Sydney', label: 'Sydney' },
  { value: 'Australia/Melbourne', label: 'Melbourne' },
  { value: 'Pacific/Auckland', label: 'Auckland' },
];

function parseIntervalString(raw) {
  if (!raw) return { amount: '', unit: 'h' };
  const trimmed = raw.trim().toLowerCase();
  const bdMatch = trimmed.match(/^(\d+)\s*bd$/);
  if (bdMatch) return { amount: bdMatch[1], unit: 'bd' };
  const singleMatch = trimmed.match(/^(\d+)\s*(m|h|d)$/);
  if (singleMatch) return { amount: singleMatch[1], unit: singleMatch[2] };
  return { amount: '', unit: 'h', raw };
}

function buildIntervalString(amount, unit) {
  const n = parseInt(amount, 10);
  if (isNaN(n) || n <= 0) return '';
  return `${n}${unit}`;
}

function buildRecurringStatusStyles(p) {
  return {
    ACTIVE:    { ...p.teal, label: 'Active' },
    FIRING:    { ...p.blue, label: 'Firing' },
    PAUSED:    { ...p.amber, label: 'Paused' },
    COMPLETED: { ...p.indigo, label: 'Completed' },
    CANCELLED: { ...p.zinc, label: 'Cancelled' },
    ERROR:     { ...p.red, label: 'Error' },
  };
}

export function RecurringScheduleStatusBadge({ status }) {
  const _sp = useStatusPalette();
  const styles = buildRecurringStatusStyles(_sp);
  const s = styles[status] || styles.ACTIVE;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        height: '20px',
        px: '6px',
        borderRadius: '5px',
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
      }}
    >
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: s.color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 500, color: s.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
        {s.label}
      </Typography>
    </Box>
  );
}

function humanizeInterval(raw) {
  if (!raw) return raw;
  const { amount, unit, raw: unparsed } = parseIntervalString(raw);
  if (unparsed) return raw;
  const n = parseInt(amount, 10);
  if (isNaN(n)) return raw;
  const unitLabel = INTERVAL_UNITS.find((u) => u.value === unit);
  if (!unitLabel) return raw;
  const label = unitLabel.label.toLowerCase();
  return n === 1 ? label.replace(/s$/, '') : `${n} ${label}`;
}

export function formatRecurrenceDescription(item) {
  if (!item) return '';
  let base = 'Recurring';
  if (item.recurrenceType === 'every_interval' && item.intervalDuration) {
    base = `Every ${humanizeInterval(item.intervalDuration)}`;
  } else if (item.recurrenceType === 'on_days_at_time') {
    const dayNames = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
    const days = (item.daysOfWeek || []).map((d) => dayNames[d] || d).join(', ');
    const time = item.timeOfDay || '';
    base = `Every ${days}${time ? ` at ${time}` : ''}`;
  }
  if (item.maxOccurrences != null) {
    base += ` (${item.maxOccurrences}x)`;
  } else if (item.endAt) {
    const d = new Date(item.endAt);
    base += ` (until ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`;
  }
  return base;
}

const fieldInputSx = {
  fontSize: '14px',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '8px',
  px: 1.5,
  py: 1,
  mb: 2,
  backgroundColor: 'background.paper',
  '&:focus-within': { borderColor: 'text.disabled' },
};

export default function RecurringScheduleDialog({ item, open, onClose, onEdit, onCancel, onPause, onResume }) {
  const _sp = useStatusPalette();
  const [editMessage, setEditMessage] = useState('');
  const [editRecurrenceType, setEditRecurrenceType] = useState('every_interval');
  const [editIntervalAmount, setEditIntervalAmount] = useState('');
  const [editIntervalUnit, setEditIntervalUnit] = useState('h');
  const [editDaysOfWeek, setEditDaysOfWeek] = useState([]);
  const [editTimeOfDay, setEditTimeOfDay] = useState('');
  const [editTimezone, setEditTimezone] = useState('');
  const [editRepeatMode, setEditRepeatMode] = useState('count');
  const [editMaxOccurrences, setEditMaxOccurrences] = useState('');
  const [editEndAt, setEditEndAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [pausing, setPausing] = useState(false);

  useEffect(() => {
    if (open && item) {
      setEditMessage(item.message || '');
      setEditRecurrenceType(item.recurrenceType || 'every_interval');
      const parsed = parseIntervalString(item.intervalDuration);
      setEditIntervalAmount(parsed.amount);
      setEditIntervalUnit(parsed.unit);
      setEditDaysOfWeek(item.daysOfWeek || []);
      setEditTimeOfDay(item.timeOfDay || '');
      setEditTimezone(item.timezone || 'UTC');
      if (item.endAt) {
        setEditRepeatMode('until');
        setEditEndAt(new Date(item.endAt).toISOString().slice(0, 16));
      } else {
        setEditRepeatMode('count');
        setEditEndAt('');
      }
      setEditMaxOccurrences(item.maxOccurrences != null ? String(item.maxOccurrences) : '');
      setSaving(false);
      setCancelling(false);
      setPausing(false);
    }
  }, [open, item?.scheduleId]);

  if (!item) return null;

  const isEditable = item.status === 'ACTIVE' || item.status === 'PAUSED';
  const isActive = item.status === 'ACTIVE';
  const isPaused = item.status === 'PAUSED';

  const handleSave = async () => {
    const payload = {};
    if (editMessage.trim() && editMessage.trim() !== (item.message || '')) payload.message = editMessage.trim();
    if (editRecurrenceType !== item.recurrenceType) payload.recurrenceType = editRecurrenceType;
    if (editRecurrenceType === 'every_interval') {
      const built = buildIntervalString(editIntervalAmount, editIntervalUnit);
      if (built && built !== (item.intervalDuration || '')) {
        payload.intervalDuration = built;
      }
    }
    if (editRecurrenceType === 'on_days_at_time') {
      const oldDays = [...(item.daysOfWeek || [])].sort().join(',');
      const newDays = [...editDaysOfWeek].sort().join(',');
      if (newDays !== oldDays) payload.daysOfWeek = editDaysOfWeek;
      if (editTimeOfDay !== (item.timeOfDay || '')) payload.timeOfDay = editTimeOfDay;
    }
    if (editTimezone !== (item.timezone || '')) payload.timezone = editTimezone;
    if (editRepeatMode === 'count') {
      const parsed = parseInt(editMaxOccurrences, 10);
      if (!isNaN(parsed) && parsed !== (item.maxOccurrences ?? null)) payload.maxOccurrences = parsed;
      if (item.endAt && !isNaN(parsed)) payload.endAt = '';
    } else {
      if (editEndAt) {
        const iso = new Date(editEndAt).toISOString();
        const oldIso = item.endAt ? new Date(item.endAt).toISOString() : '';
        if (iso !== oldIso) payload.endAt = iso;
      }
      if (item.maxOccurrences != null) payload.maxOccurrences = null;
    }

    if (Object.keys(payload).length > 0 && onEdit) {
      setSaving(true);
      await onEdit(item.scheduleId, payload);
      setSaving(false);
    }
    onClose();
  };

  const handleCancel = async () => {
    if (!onCancel) return;
    setCancelling(true);
    await onCancel(item.scheduleId);
    setCancelling(false);
    onClose();
  };

  const handlePauseResume = async () => {
    setPausing(true);
    if (isActive && onPause) {
      await onPause(item.scheduleId);
    } else if (isPaused && onResume) {
      await onResume(item.scheduleId);
    }
    setPausing(false);
    onClose();
  };

  const toggleDay = (dayValue) => {
    setEditDaysOfWeek((prev) =>
      prev.includes(dayValue) ? prev.filter((d) => d !== dayValue) : [...prev, dayValue].sort()
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
          fontSize: '15px',
          fontWeight: 600,
          color: 'text.primary',
        }}
      >
        <EventRepeatOutlinedIcon sx={{ fontSize: 20, color: _sp.teal.color }} />
        Recurring schedule
        <Box sx={{ flex: 1 }} />
        <RecurringScheduleStatusBadge status={item.status} />
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important', pb: 1 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
          Message
        </Typography>
        {isEditable ? (
          <InputBase
            value={editMessage}
            onChange={(e) => setEditMessage(e.target.value)}
            multiline
            minRows={2}
            maxRows={8}
            fullWidth
            sx={fieldInputSx}
          />
        ) : (
          <Typography sx={{ fontSize: '14px', color: 'text.primary', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 2, px: 0.5 }}>
            {item.message || 'No message'}
          </Typography>
        )}

        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
          Recurrence
        </Typography>
        {isEditable ? (
          <>
            <Select
              value={editRecurrenceType}
              onChange={(e) => setEditRecurrenceType(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 1.5, fontSize: '14px', borderRadius: '8px' }}
            >
              <MenuItem value="every_interval">Repeat after interval</MenuItem>
              <MenuItem value="on_days_at_time">On specific days at a time</MenuItem>
            </Select>

            {editRecurrenceType === 'every_interval' && (
              <>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.5 }}>
                  Repeat every
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <InputBase
                    value={editIntervalAmount}
                    onChange={(e) => setEditIntervalAmount(e.target.value.replace(/\D/g, ''))}
                    placeholder="1"
                    inputProps={{ inputMode: 'numeric', style: { textAlign: 'center' } }}
                    sx={{
                      ...fieldInputSx,
                      mb: 0,
                      width: '80px',
                      flexShrink: 0,
                    }}
                  />
                  <Select
                    value={editIntervalUnit}
                    onChange={(e) => setEditIntervalUnit(e.target.value)}
                    size="small"
                    sx={{ flex: 1, fontSize: '14px', borderRadius: '8px' }}
                  >
                    {INTERVAL_UNITS.map((u) => (
                      <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                    ))}
                  </Select>
                </Box>
              </>
            )}

            {editRecurrenceType === 'on_days_at_time' && (
              <>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.5 }}>
                  Days of week
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                  {DAY_OPTIONS.map((d) => (
                    <FormControlLabel
                      key={d.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={editDaysOfWeek.includes(d.value)}
                          onChange={() => toggleDay(d.value)}
                          sx={{ p: '4px', '&.Mui-checked': { color: _sp.teal.color } }}
                        />
                      }
                      label={d.label}
                      sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.5 }}>
                  Time of day (HH:mm)
                </Typography>
                <TextField
                  type="time"
                  value={editTimeOfDay}
                  onChange={(e) => setEditTimeOfDay(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '14px',
                    },
                  }}
                />
              </>
            )}

            <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.5 }}>
              Timezone
            </Typography>
            <Select
              value={
                COMMON_TIMEZONES.some((tz) => tz.value === editTimezone)
                  ? editTimezone
                  : '__custom__'
              }
              onChange={(e) => {
                if (e.target.value !== '__custom__') setEditTimezone(e.target.value);
              }}
              size="small"
              fullWidth
              sx={{ mb: 1.5, fontSize: '14px', borderRadius: '8px' }}
              renderValue={(val) => {
                if (val === '__custom__') return editTimezone;
                const found = COMMON_TIMEZONES.find((tz) => tz.value === val);
                return found ? `${found.label} (${found.value})` : val;
              }}
            >
              {COMMON_TIMEZONES.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label} ({tz.value})
                </MenuItem>
              ))}
              {!COMMON_TIMEZONES.some((tz) => tz.value === editTimezone) && (
                <MenuItem value="__custom__" disabled>
                  {editTimezone} (custom)
                </MenuItem>
              )}
            </Select>

            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
              Repeat limit
            </Typography>
            <Select
              value={editRepeatMode}
              onChange={(e) => setEditRepeatMode(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 1.5, fontSize: '14px', borderRadius: '8px' }}
            >
              <MenuItem value="count">Number of times</MenuItem>
              <MenuItem value="until">Until date/time</MenuItem>
            </Select>
            {editRepeatMode === 'count' ? (
              <>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.5 }}>
                  Max occurrences
                </Typography>
                <InputBase
                  value={editMaxOccurrences}
                  onChange={(e) => setEditMaxOccurrences(e.target.value.replace(/\D/g, ''))}
                  fullWidth
                  placeholder="e.g. 10"
                  inputProps={{ inputMode: 'numeric' }}
                  sx={fieldInputSx}
                />
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.5 }}>
                  End date/time
                </Typography>
                <TextField
                  type="datetime-local"
                  value={editEndAt}
                  onChange={(e) => setEditEndAt(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '14px',
                    },
                  }}
                />
              </>
            )}
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: '14px', color: 'text.primary', mb: 1, px: 0.5 }}>
              {formatRecurrenceDescription(item)}
              {item.timezone ? ` (${item.timezone})` : ''}
            </Typography>
            {item.maxOccurrences != null && (
              <Typography sx={{ fontSize: '13px', color: 'text.disabled', mb: 1, px: 0.5 }}>
                Repeats {item.maxOccurrences} time{item.maxOccurrences !== 1 ? 's' : ''}
              </Typography>
            )}
            {item.endAt && (
              <Typography sx={{ fontSize: '13px', color: 'text.disabled', mb: 1, px: 0.5 }}>
                Until {new Date(item.endAt).toLocaleString(undefined, {
                  weekday: 'short', month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit',
                })}
              </Typography>
            )}
          </>
        )}

        <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.25 }}>
              Next occurrence
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>
              {item.nextResumeAt
                ? new Date(item.nextResumeAt).toLocaleString(undefined, {
                    weekday: 'short', month: 'short', day: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  })
                : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.25 }}>
              Times fired
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>
              {item.occurrenceCount ?? 0}
              {item.maxOccurrences != null ? ` / ${item.maxOccurrences}` : ''}
            </Typography>
          </Box>
        </Box>

        {item.reason && (
          <>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', mb: 0.75, mt: 1.5 }}>
              Reason
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.disabled', fontStyle: 'italic', mb: 1, px: 0.5 }}>
              {item.reason}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, justifyContent: 'space-between' }}>
        {isEditable ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={cancelling ? undefined : handleCancel}
              disabled={cancelling || saving || pausing}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 600,
                color: _sp.red.color,
                '&:hover': { backgroundColor: _sp.red.bg },
              }}
            >
              {cancelling ? 'Cancelling...' : 'Cancel schedule'}
            </Button>
            <Button
              onClick={pausing ? undefined : handlePauseResume}
              disabled={cancelling || saving || pausing}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 600,
                color: isActive ? _sp.amber.color : _sp.teal.color,
                '&:hover': { backgroundColor: isActive ? _sp.amber.bg : _sp.teal.bg },
              }}
            >
              {pausing ? (isActive ? 'Pausing...' : 'Resuming...') : isActive ? 'Pause' : 'Resume'}
            </Button>
          </Box>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              color: 'text.disabled',
            }}
          >
            Close
          </Button>
          {isEditable && (
            <Button
              onClick={saving ? undefined : handleSave}
              disabled={saving || cancelling || pausing}
              variant="contained"
              disableElevation
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 600,
                backgroundColor: _sp.teal.color,
                borderRadius: '8px',
                px: 2.5,
                '&:hover': { backgroundColor: _sp.teal.color, opacity: 0.85 },
                '&.Mui-disabled': { backgroundColor: _sp.teal.color, opacity: 0.5, color: '#fff' },
              }}
            >
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
